import { fromUint8Array } from 'js-base64'
import { gzip } from 'pako'

import { regexEscape } from '../escape-utils'

import { Tool } from './base'
import {
  escapeRx,
  etaFieldLookup,
  InlineGroups,
  makeRenderer,
  normalizeList,
  RenderContext,
  RenderOut,
  scalarToString,
} from './render/render-kit'
import { DataDisplayTemplate } from './render/rtd/datadisplay.types'
import { wrapRendererWithDataDisplay } from './render/rtd/datadisplay.utils'
import { NormalizedDataSource } from './render/rtd/datasource.types'
import {
  joinWithAnd,
  wrapRendererWithDatasource,
} from './render/rtd/datasource.utils'
import { googleSecOpsMap } from './render/rtd/tool-datasource-mappings/google-secops.datasource'
import { AstPredicate, ValueOrList } from './render/types'

const eta = (f: string) => `{{!=${f}!}}`

export const googleSecOpsDataDisplayTemplate: DataDisplayTemplate = {
  projection: null, // YARA-L does not support standalone projection in filter context

  groupBy: (groupFields, aggClauses) => {
    const matchField = groupFields.map(eta).join(',\n  ')
    const outcomeLines = [
      `$first_seen = timestamp.get_timestamp(min(metadata.event_timestamp.seconds))`,
      `$last_seen = timestamp.get_timestamp(max(metadata.event_timestamp.seconds))`,
      ...aggClauses,
    ]
    const countAliases = aggClauses
      .filter((l) => /=\s*count[_(]/.test(l))
      .map((l) => l.split('=')[0].trim())
    const lines = [
      `match:`,
      `  ${matchField}`,
      ``,
      `outcome:`,
      ...outcomeLines.map((l) => `  ${l}`),
    ]
    if (countAliases.length) {
      lines.push(``, `order:`, `  ${countAliases.join(', ')} desc`)
    }
    lines.push(``, `limit:`, `  100`)
    return lines.join('\n')
  },

  timeBucket: null,

  aggFns: {
    values: (field, alias) => `$${alias} = array_distinct(${eta(field)})`,
    collect: (field, alias) => `$${alias} = array_distinct(${eta(field)})`,
    count: (field, alias) => `$${alias} = count(${eta(field)})`,
    dcount: (field, alias) => `$${alias} = count_distinct(${eta(field)})`,
    first: (field, alias) => `$${alias} = min(${eta(field)})`,
    last: (field, alias) => `$${alias} = max(${eta(field)})`,
    min: (field, alias) => `$${alias} = min(${eta(field)})`,
    max: (field, alias) => `$${alias} = max(${eta(field)})`,
    sum: (field, alias) => `$${alias} = sum(${eta(field)})`,
    avg: (field, alias) => `$${alias} = avg(${eta(field)})`,
  },

  fieldAlias: null,

  join: (body, ddClause) => `${body}\n\n${ddClause}`,
}

export { googleSecOpsMap }

export class GoogleSecOpsTool extends Tool {
  public readonly toolName = 'Google SecOps'
  public readonly toolKind = 'siem' as const

  // Google SecOps UDM maps every Windows event source to the same UDM field
  // names — the `metadata.event_type` prelude already scopes to the applicable
  // events, so declaring sysmon + wineventlog separately produces duplicate
  // output. Accepting that duplication intentionally until SecOps is
  // reclassified as an XDR tool (at which point it bypasses log-category
  // gating and emits once).
  public override supportedLogCategories(): string[] {
    return ['endpoint:sysmon', 'endpoint:wineventlog', 'edr']
  }

  public readonly toolDefaultCCMFieldMappings = {
    access: 'target.process.access_mask',
    accesses: 'target.process.access_mask',
    account_id: 'principal.user.windows_sid',
    account_name: 'src.user.user_display_name',
    action: 'security_result.action',
    alert_type: '',
    application_name: 'network.http.user_agent',
    authentication_method: 'src.application',
    bytes_in: 'network.received_bytes',
    bytes_out: 'network.sent_bytes',
    cipher_suite: '',
    client_issuer: '',
    client_subject: '',
    cloud_platform: '',
    dest_domain: 'target.hostname',
    dest_hostname: 'target.hostname',
    dest_ip: 'target.ip',
    dest_mac: 'target.mac',
    dest_port: 'target.port',
    details: 'metadata.description',
    dns_answer: 'network.dns.answers.name',
    dns_query: 'network.dns.questions.name',
    dns_record_type: 'network.dns.questions.type',
    domain: 'target.hostname',
    email_recipient: 'network.email.to',
    email_sender: 'network.email.from',
    email_subject: 'network.email.subject',
    error_code: 'security_result.description',
    error_message: 'security_result.description',
    event_id: 'metadata.product_event_type',
    event_name: 'metadata.description',
    event_type: 'metadata.event_type',
    file_name: 'target.file.full_path',
    file_path: 'target.file.full_path',
    group_id: 'target.user.group_identifiers',
    hostname: 'principal.hostname',
    identity_principal_id: 'principal.user.windows_sid',
    instance_id: '',
    instance_name: '',
    instance_type: '',
    logon_process: 'target.process.file.full_path',
    logon_type: 'extensions.auth.mechanism',
    md5: 'target.process.file.md5',
    message: 'security_result.description',
    mfa_auth: '',
    name: 'target.application',
    object_loaded: 'target.process.file.full_path',
    object_name: 'target.file.full_path',
    parent_process_cmdline: 'principal.process.command_line',
    parent_process_id: 'principal.process.pid',
    parent_process_name: 'principal.process.file.full_path',
    parent_process_path: 'principal.process.file.full_path',
    pipe_name: 'target.resource.name',
    post_body: '',
    process_cmdline: 'target.process.command_line',
    process_id: 'target.process.pid',
    process_name: 'target.process.file.full_path',
    process_path: 'target.process.file.full_path',
    protocol: 'network.ip_protocol',
    record_id: 'metadata.product_log_id',
    referrer: '',
    region: '',
    registry_key_name: 'target.registry.registry_value_name',
    registry_new_value_name: 'target.registry.registry_value_data',
    registry_old_value_name: 'target.registry.registry_value_data',
    registry_path: 'target.registry.registry_key',
    registry_value_name: 'target.registry.registry_value_data',
    request_method: 'network.http.method',
    request_parameters: 'target.url',
    service: 'target.application',
    service_file_path: 'target.process.file.full_path',
    service_name: 'target.application',
    severity: 'security_result.severity',
    sha256: 'target.process.file.sha256',
    sid: 'principal.user.windows_sid',
    source_user_role: 'principal.user.attribute.roles.name',
    src_hostname: 'principal.hostname',
    src_ip: 'principal.ip',
    src_mac: 'principal.mac',
    src_port: 'principal.port',
    status_code: 'network.http.response_code',
    storage_property_name: '',
    storage_target_object: '',
    target_path: 'target.file.full_path',
    task_category: 'target.resource.resource_type',
    title: '',
    uri: 'target.url',
    url: 'target.url',
    user_access_id: 'target.resource.attribute.permissions.name',
    user_agent: 'network.http.user_agent',
    user_role: 'principal.user.attribute.roles.description',
    user_type: 'principal.user.attribute.roles.description',
    username: 'principal.user.userid',
  }

  public generateSearchPermalink(
    rootURL: string, //https://ggltp.backstory.chronicle.security/
    renderedContent: string,
  ): string {
    const encodedQuery = encodeURIComponent(renderedContent)

    // Generate time range (last 24 hours as default)
    const endTime = new Date().toISOString()
    const startTime = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString()

    return `${rootURL}search?query=${encodedQuery}&startTime=${startTime}&endTime=${endTime}`
  }

  public pivotEscape(key: string, value: string): string {
    return regexEscape(value)
  }

  /** Notes & Implementation
   * Grouping
   * - InlineGroups: no root parentheses; nested groups parenthesized.
   *
   * Fields
   * - Eta wrapping: {{!=<mapped>!}} from toolTuningFields["google_secops"].
   *
   * Quoting / Escaping
   * - Strings: unquoted by default (per spec).
   * - Equality operators require **double quotes** for strings (numbers bare).
   * - Numbers: bare.
   * - Backslashes and quotes escaped inside quoted strings (equality).
   *
   * Regex
   * - Allowed; append " nocase".
   * - contains (multi): single alternation /(v1|v2)/ nocase.
   * - startswith/endswith (multi): OR-chain of single regexes.
   *
   * Equality
   * - single: F = "v"   (strings quoted) / F = 123 (numbers bare)
   * - multi:  (F = "v1" OR F = "v2")
   *
   * Inequality
   * - single: F != "v"  (strings quoted) / F != 123 (numbers bare)
   * - multi:  (F != "v1" AND F != "v2")
   *
   * Null / Empty
   * - isnull     → isnull(F)
   * - isnotnull  → isnotnull(F)
   * - isempty    → isempty(F)
   * - isnotempty → isnotempty(F)
   */

  public renderTuning = makeRenderer({
    tool: this.toolName,
    groupStrategy: InlineGroups,
    renderContext: {
      andGlue: ' AND ',
      orGlue: ' OR ',
      wrapField: (f) => etaFieldLookup(f), // safe Eta key-lookup (SSTI-hardened)
      // We won’t use the global quote/list for equality; we implement smart quoting below.
    },

    formatPredicate: (p: AstPredicate, ctx: RenderContext): string => {
      const F = ctx.wrapField(ctx.mapField(p.field))

      // Preserve raw types to decide number vs string; also prepare stringified list.
      const raw: ValueOrList | undefined = p.value
      const rawList =
        raw == null ? [undefined] : Array.isArray(raw) ? raw : [raw]
      const vals = normalizeList(p.value).map(scalarToString)
      const isMulti = vals.length > 1

      // Smart equality-quoting (numbers bare; strings double-quoted with escapes).
      const Qeq = (s: string, rv: any) =>
        typeof rv === 'number'
          ? String(rv)
          : `"${String(s).replace(/\\/g, '\\\\').replace(/"/g, '\\"')}"`

      // For comparisons (lt/lte/gt/gte): numbers bare; strings unquoted by spec.
      const Qcmp = (s: string, rv: any) =>
        typeof rv === 'number' ? String(rv) : s

      // Regex builders
      const rxContains = (v: string) => `/${escapeRx(v)}/ nocase`
      const rxStarts = (v: string) => `/^${escapeRx(v)}/ nocase`
      const rxEnds = (v: string) => `/${escapeRx(v)}$/ nocase`

      // Multi regex – alternation for contains
      const rxContainsAlt = (vals: string[]) =>
        `/${vals.map(escapeRx).join('|')}/ nocase`

      // Helpers for OR/AND groups
      const OR = (parts: string[]) => `(${parts.join(ctx.orGlue)})`
      const AND = (parts: string[]) => `(${parts.join(ctx.andGlue)})`
      const NOT = (clause: string) => `NOT ${clause}`

      /* null/empty */
      switch (p.op) {
        case 'isnull':
          return `${F} = ""`
        case 'isnotnull':
          return `${F} != ""`
        case 'isempty':
          return `${F} = ""`
        case 'isnotempty':
          return `${F} != ""`
      }

      /* equality / inequality */
      if (p.op === 'eq') {
        if (!isMulti) return `${F} = ${Qeq(vals[0], rawList[0])}`
        const parts = vals.map((s, i) => `${F} = ${Qeq(s, rawList[i])}`)
        return OR(parts) // F = "v1" OR F = "v2"
      }
      if (p.op === 'neq') {
        if (!isMulti) return NOT(`${F} = ${Qeq(vals[0], rawList[0])}`)
        const parts = vals.map((s, i) => `${F} = ${Qeq(s, rawList[i])}`)
        return NOT(OR(parts)) // F != "v1" AND F != "v2"
      }

      /* contains */
      if (p.op === 'contains') {
        if (!isMulti) return `${F} = ${rxContains(vals[0])}`
        return `${F} = ${rxContainsAlt(vals)}` // /(v1|v2)/ nocase
      }
      if (p.op === 'doesnotcontain') {
        if (!isMulti) return NOT(`${F} = ${rxContains(vals[0])}`)
        return NOT(`${F} = ${rxContainsAlt(vals)}`)
      }
      /* Added Regex Parsing Due to Google SecOps Bizzare Rendering of Starts/Ends With */
      // Google SecOpts doesn't like when you combine values, like in multi-value contains with | between them
      // Each must be or separated
      if (p.op === 'startswith') {
        if (!isMulti) return `${F} = ${rxStarts(vals[0])}`
        const parts = vals.map((s) => `${F} = ${rxStarts(s)}`)
        return OR(parts) // F = /^v1/ nocase OR F = /^v2/ nocase
      }
      if (p.op === 'endswith') {
        if (!isMulti) return `${F} = ${rxEnds(vals[0])}`
        const parts = vals.map((s) => `${F} = ${rxEnds(s)}`)
        return OR(parts) // F = /v1$/ nocase OR F = /v2$/ nocase
      }

      /* comparisons (values unquoted per spec) */
      switch (p.op) {
        case 'lt':
          return `${F} < ${Qcmp(vals[0], rawList[0])}`
        case 'lte':
          return `${F} <= ${Qcmp(vals[0], rawList[0])}`
        case 'gt':
          return `${F} > ${Qcmp(vals[0], rawList[0])}`
        case 'gte':
          return `${F} >= ${Qcmp(vals[0], rawList[0])}`
      }

      // Safe default
      if (isMulti) {
        const parts = vals.map((s, i) => `${F} = ${Qeq(s, rawList[i])}`)
        return OR(parts)
      }
      return `${F} = ${Qeq(vals[0], rawList[0])}`
    },
    finalize: (out: RenderOut, renderContext: RenderContext): string => {
      const base =
        out.kind === 'text' ? out.text : out.lines.join(renderContext.andGlue)
      if (!base) return ''
      return `(${base})`
    },
  })
  public readonly tuningText =
    'Append tuning logic to the end of the hunt logic. In some cases the hunt logic will need to be encapsulated in parenthesis, when tuning logic does not work as desired.'

  public override readonly datasourceMap = googleSecOpsMap
  public readonly dataDisplayTemplate = googleSecOpsDataDisplayTemplate

  /**
   * Emit the Google SecOps event_type prelude.
   * Single event:  metadata.event_type = "PROCESS_LAUNCH"
   * Multi-event:   (metadata.event_type = "FILE_CREATION" or\n  metadata.event_type = "FILE_MODIFICATION")
   */
  public emitGoogleSecOpsDatasource(
    ds: NormalizedDataSource | undefined,
  ): string {
    if (!ds?.events?.length) return ''
    const field = 'metadata.event_type'
    const Q = (s: string) => JSON.stringify(s)
    if (ds.events.length === 1) {
      return `${field} = ${Q(ds.events[0])}`
    }
    return `(${ds.events.map((e) => `${field} = ${Q(e)}`).join(` or\n  `)})`
  }

  /** Emit prelude AND tuning body + DataDisplay clause. */
  public readonly renderGoogleSecOps = wrapRendererWithDataDisplay(
    wrapRendererWithDatasource(
      this.renderTuning,
      this.toolName,
      (ds) => this.emitGoogleSecOpsDatasource(ds),
      joinWithAnd,
      this.tuningText,
    ),
    this.toolName,
    googleSecOpsDataDisplayTemplate,
  )
}
